import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Terminal, ChevronRight, Command } from 'lucide-react';

const TerminalInput = ({ onSubmit, placeholder = 'Type your command...' }) => {
  const [input, setInput] = useState('');
  const [history, setHistory] = useState([
    'Welcome to Quavrion Terminal v2.0',
    'Type help for available commands',
    'Ready for input...'
  ]);
  const [isTyping, setIsTyping] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const newHistory = [...history, `$ ${input}`];
    
    // Simulate response
    setTimeout(() => {
      const responses = {
        help: 'Available commands: services, demo, contact, docs, status',
        services: 'AI/ML · Cloud Infrastructure · Blockchain · Cybersecurity · Data Analytics',
        demo: 'Starting demo session... Visit demo.quavrion.com',
        contact: 'Contact team@quavrion.com or call +1 (555) TECH-123',
        status: 'All systems operational. Uptime: 99.9%',
        clear: 'Clearing terminal...'
      };

      const response = responses[input.toLowerCase()] || `Unknown command: ${input}. Type help for commands.`;
      setHistory([...newHistory, response]);
    }, 500);

    if (onSubmit) onSubmit(input);
    setInput('');
    setIsTyping(true);
    setTimeout(() => setIsTyping(false), 500);
  };

  const quickCommands = [
    { cmd: 'services', desc: 'View our services' },
    { cmd: 'demo', desc: 'Start live demo' },
    { cmd: 'contact', desc: 'Get in touch' },
    { cmd: 'status', desc: 'System status' }
  ];

  return (
    <div className="w-full">
      {/* Terminal Header */}
      <div className="flex items-center justify-between mb-4 px-1">
        <div className="flex items-center space-x-3">
          <div className="flex space-x-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <Terminal className="w-4 h-4 text-cyan-400" />
          <span className="text-sm font-mono text-gray-400">terminal</span>
        </div>
        <Command className="w-4 h-4 text-gray-500" />
      </div>

      {/* Terminal Output */}
      <div className="bg-gray-900/80 rounded-lg p-4 mb-4 h-40 overflow-y-auto font-mono text-sm">
        {history.map((line, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.05 }}
            className={`mb-1 ${line.startsWith('$') ? 'text-cyan-300' : 'text-gray-300'}`}
          >
            {line}
          </motion.div>
        ))}
        {isTyping && (
          <div className="text-cyan-400 animate-pulse">
            Processing...
          </div>
        )}
      </div>

      {/* Input Area */}
      <form onSubmit={handleSubmit} className="relative">
        <motion.div
          animate={{ boxShadow: isTyping ? '0 0 20px rgba(6, 182, 212, 0.3)' : 'none' }}
          className="flex items-center bg-black border border-gray-700 rounded-lg px-4 py-3"
        >
          <ChevronRight className="w-4 h-4 text-green-400 mr-3 flex-shrink-0" />
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={placeholder}
            className="flex-1 bg-transparent text-gray-100 font-mono text-sm focus:outline-none placeholder-gray-500"
            autoComplete="off"
            spellCheck="false"
          />
          <button
            type="submit"
            className="ml-3 px-3 py-1 bg-gradient-to-r from-cyan-600 to-blue-600 rounded text-xs font-medium hover:from-cyan-700 hover:to-blue-700 transition-all"
          >
            Execute
          </button>
        </motion.div>
      </form>

      {/* Quick Commands */}
      <div className="mt-4">
        <div className="text-xs text-gray-500 mb-2">Quick commands:</div>
        <div className="flex flex-wrap gap-2">
          {quickCommands.map(({ cmd, desc }) => (
            <button
              key={cmd}
              onClick={() => setInput(cmd)}
              className="px-3 py-1.5 bg-gray-800/50 hover:bg-gray-700/50 rounded text-xs font-mono text-cyan-300 hover:text-cyan-200 transition-all"
            >
              {cmd}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TerminalInput;